import { uploadMedia } from '@/features/atendimento/api';
import { ImageLightbox } from '@/features/atendimento/components/ImageLightbox';
import { PdfPreviewModal } from '@/features/atendimento/components/PdfPreviewModal';
import { compressImage } from '@/features/atendimento/lib/compress-image';
import { getSocket } from '@/features/atendimento/socket';
import { type UserRow, listUsers } from '@/features/configuracoes/api';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  CheckCheck,
  ChevronDown,
  FileText,
  Loader2,
  MessagesSquare,
  Paperclip,
  Search,
  SendHorizontal,
  Upload,
  User,
  X,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  type InternalMessage,
  getInternalMediaUrl,
  markInternalRead,
  sendInternalMessage,
} from '../api';
import {
  CONVERSATIONS_KEY,
  type InternalThreadData,
  threadKey,
  tirarEntregaAntecipada,
  useInternalChatDisabled,
  useInternalChatRealtime,
  useInternalConversations,
  useInternalThread,
  useInternalUnreadTotal,
} from '../hooks';
import { useInternalChatStore } from '../store';

/**
 * Botão do chat interno na Topbar (badge de não-lidas) + popup ancorado.
 * O listener realtime global vive AQUI (Topbar monta a sessão inteira) —
 * som/notificação funcionam mesmo com o popup fechado.
 */
export function InternalChatButton() {
  useInternalChatRealtime();
  const open = useInternalChatStore((s) => s.open);
  const setOpen = useInternalChatStore((s) => s.setOpen);
  const unread = useInternalUnreadTotal();
  const disabled = useInternalChatDisabled();

  // Feature desativada pro tenant (gate do Super) — some por completo.
  if (disabled) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`relative flex h-[36px] w-[36px] items-center justify-center rounded-md transition hover:bg-n-100 ${
          open ? 'bg-brand-50 text-brand-600' : 'text-n-600'
        }`}
        title="Chat interno"
        aria-label="Chat interno"
        aria-expanded={open}
      >
        <MessagesSquare size={18} strokeWidth={1.75} />
        {unread > 0 && (
          <span className="-right-1 -top-1 absolute inline-flex min-w-[18px] items-center justify-center rounded-pill border-2 border-n-0 bg-danger-500 px-1 text-center font-bold text-[10px] text-white leading-[14px]">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && <InternalChatPopup />}
    </div>
  );
}

function InternalChatPopup() {
  const setOpen = useInternalChatStore((s) => s.setOpen);
  const activePeerId = useInternalChatStore((s) => s.activePeerId);
  const lightboxOpen = useInternalChatStore((s) => s.lightboxOpen);
  const pos = useInternalChatStore((s) => s.pos);
  const setPos = useInternalChatStore((s) => s.setPos);
  const panelRef = useRef<HTMLDivElement>(null);

  // Arrastar pelo CABEÇALHO (elementos com data-drag-handle): mouse-down fora
  // de botão/input inicia o drag; solta em qualquer lugar. Posição vira
  // `fixed` (limitada à tela) e fica lembrada na sessão. Mouse events (não
  // pointer): cobrem qualquer desktop e o preventDefault mata a seleção de
  // texto durante o arrasto.
  const onPanelMouseDown = (e: React.MouseEvent): void => {
    const target = e.target as HTMLElement;
    if (!target.closest('[data-drag-handle]')) return;
    if (target.closest('button, input, textarea, a')) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const dx = e.clientX - rect.left;
    const dy = e.clientY - rect.top;
    const onMove = (ev: MouseEvent): void => {
      const w = panel.offsetWidth;
      const x = Math.min(Math.max(8, ev.clientX - dx), window.innerWidth - w - 8);
      const y = Math.min(Math.max(8, ev.clientY - dy), window.innerHeight - 64);
      setPos({ x, y });
    };
    const onUp = (): void => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    e.preventDefault();
  };

  // Esc fecha o popup — MENOS com o lightbox aberto (aí o Esc é dele).
  useEscapeClose(!lightboxOpen, () => setOpen(false));

  // SEM fechar-ao-clicar-fora (pedido do dono): o popup é uma janelinha
  // flutuante — o atendente arrasta pra um canto e segue trabalhando no
  // painel com ela aberta. Fecha só no X, no Esc ou no ícone da topbar.

  // Presença ao vivo enquanto o popup está aberto (mesma máquina do
  // TransferModal): atualiza o cache compartilhado ['settings-users'].
  const qc = useQueryClient();
  useEffect(() => {
    const socket = getSocket();
    const onPresence = (p: { user_id: number; online: boolean }): void => {
      qc.setQueryData<UserRow[]>(['settings-users'], (prev) =>
        prev?.map((u) => (u.id === p.user_id ? { ...u, online: p.online } : u)),
      );
    };
    socket.on('presence.changed', onPresence);
    return () => {
      socket.off('presence.changed', onPresence);
    };
  }, [qc]);

  return (
    <div
      ref={panelRef}
      onMouseDown={onPanelMouseDown}
      className={`z-50 flex h-[550px] max-h-[calc(100vh-90px)] w-[350px] max-w-[94vw] flex-col overflow-hidden rounded-lg border border-n-200 bg-n-0 shadow-lg ${
        pos ? 'fixed' : 'absolute right-0 top-12'
      }`}
      style={pos ? { left: pos.x, top: pos.y } : undefined}
    >
      {activePeerId === null ? <ListView /> : <ThreadView peerId={activePeerId} />}
    </div>
  );
}

// ───────────────────────── Lista de colegas ─────────────────────────

function ListView() {
  const setOpen = useInternalChatStore((s) => s.setOpen);
  const openThread = useInternalChatStore((s) => s.openThread);
  const me = useAuthStore((s) => s.user?.id);
  const [search, setSearch] = useState('');

  const usersQ = useQuery({ queryKey: ['settings-users'], queryFn: () => listUsers() });
  const convsQ = useInternalConversations();
  const convByPeer = useMemo(
    () => new Map((convsQ.data ?? []).map((c) => [c.peer_user_id, c])),
    [convsQ.data],
  );

  // Colegas ativos (menos eu), filtrados pela busca; quem tem conversa
  // recente vem primeiro (ordem da última msg), o resto alfabético.
  const colleagues = useMemo(() => {
    const term = search.trim().toLowerCase();
    const base = (usersQ.data ?? []).filter(
      (u) => u.status === 'active' && u.id !== me && u.name.toLowerCase().includes(term),
    );
    return base.sort((a, b) => {
      const ca = convByPeer.get(a.id)?.last_message.created_at;
      const cb = convByPeer.get(b.id)?.last_message.created_at;
      if (ca && cb) return cb.localeCompare(ca);
      if (ca) return -1;
      if (cb) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [usersQ.data, me, search, convByPeer]);

  return (
    <>
      <div
        data-drag-handle
        className="flex flex-shrink-0 cursor-move select-none items-center justify-between border-b border-n-200 px-4 py-3"
      >
        <div>
          <h2 className="font-bold text-[15px] text-n-900">Chat interno</h2>
          <p className="text-[11px] text-n-500">Converse com a equipe</p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
          aria-label="Fechar"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex-shrink-0 px-3 py-2">
        <div className="flex items-center gap-2 rounded-md border border-n-200 bg-n-0 px-3 py-2 focus-within:border-brand-500">
          <Search size={14} strokeWidth={1.75} className="flex-shrink-0 text-n-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Localize o colega…"
            className="w-full bg-transparent text-[13px] text-n-900 outline-none"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {usersQ.isLoading && (
          <div className="px-4 py-8 text-center text-[13px] text-n-500">Carregando…</div>
        )}
        {!usersQ.isLoading && colleagues.length === 0 && (
          <div className="px-4 py-8 text-center text-[13px] text-n-500">
            Nenhum colega encontrado.
          </div>
        )}
        {colleagues.map((u) => {
          const conv = convByPeer.get(u.id);
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => openThread(u.id)}
              className="flex w-full items-center gap-3 border-b border-n-100 px-4 py-2.5 text-left transition hover:bg-n-50"
            >
              <PeerAvatar user={u} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold text-[13px] text-n-900">{u.name}</span>
                  {conv && (
                    <span className="flex-shrink-0 text-[10px] text-n-400">
                      {timeShort(conv.last_message.created_at)}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2">
                  {/* Última mensagem minha: os vistos no lugar do "Você:", como no
                      WhatsApp. */}
                  <span
                    className={`flex min-w-0 items-center gap-1 text-[11px] ${
                      u.online ? 'text-success-600' : 'text-n-400'
                    }`}
                  >
                    {conv?.last_message.from_me && (
                      <span className="flex-shrink-0 text-n-400">
                        <VistosInternos m={conv.last_message} size={12} />
                      </span>
                    )}
                    <span className="truncate">
                      {conv ? (conv.last_message.body ?? '📎 Anexo') : u.online ? 'online' : 'offline'}
                    </span>
                  </span>
                  {conv && conv.unread_count > 0 && (
                    <span className="inline-flex min-w-[18px] flex-shrink-0 items-center justify-center rounded-pill bg-danger-500 px-1 text-center font-bold text-[10px] text-white leading-[16px]">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

// Prévia LOCAL das imagens que EU enviei (mediaId → objectURL do blob que
// acabou de subir). A bolha enviada usa esses bytes da memória — aparece
// pronta na hora, sem buscar da rede (mesmo truque do outbound optimistic
// do atendimento). Vive a sessão da aba; quem recebe não tem e busca normal.
const localPreviewByMediaId = new Map<number, string>();

// ───────────────────────── Thread 1-a-1 ─────────────────────────

function ThreadView({ peerId }: { peerId: number }) {
  const setOpen = useInternalChatStore((s) => s.setOpen);
  const backToList = useInternalChatStore((s) => s.backToList);
  const setLightboxOpen = useInternalChatStore((s) => s.setLightboxOpen);
  const qc = useQueryClient();
  const usersQ = useQuery({ queryKey: ['settings-users'], queryFn: () => listUsers() });
  const peer = usersQ.data?.find((u) => u.id === peerId) ?? null;
  const threadQ = useInternalThread(peerId);
  // Páginas de 50 (pages[0] = mais recentes) achatadas em ordem cronológica.
  const items = useMemo(
    () =>
      (threadQ.data?.pages ?? [])
        .slice()
        .reverse()
        .flatMap((p) => p.items),
    [threadQ.data],
  );

  const [text, setText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState<{ src: string; name?: string } | null>(null);
  // Viewer de PDF (iframe fullscreen — mesmo PdfPreviewModal do atendimento).
  const [pdfPreview, setPdfPreview] = useState<{ url: string; filename: string } | null>(null);
  // Anexo PREPARADO (mesmo fluxo do composer padrão): o drop/clip só faz o
  // upload e deixa a prévia acima do composer; o ENVIO é no botão de enviar
  // (com legenda opcional junto). Nada sai sozinho.
  const [pending, setPending] = useState<{
    mediaId: number;
    filename: string;
    size: number;
    mime: string;
    previewUrl: string;
  } | null>(null);
  const removePending = (): void => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
  };

  // Abrir a thread zera as não-lidas desse colega (servidor + cache local).
  useEffect(() => {
    void markInternalRead(peerId).catch(() => {});
    qc.setQueryData(
      CONVERSATIONS_KEY,
      (
        prev:
          | Array<{ peer_user_id: number; unread_count: number; last_message: unknown }>
          | undefined,
      ) => prev?.map((c) => (c.peer_user_id === peerId ? { ...c, unread_count: 0 } : c)),
    );
    inputRef.current?.focus();
  }, [peerId, qc]);

  // Scroll: 3 casos distintos, tratados num effect só.
  //  - Prepend (carregou 50 antigas no topo): PRESERVA a posição visual
  //    (compensa a altura que entrou acima).
  //  - Append (msg nova no fim): cola no fim se for MINHA ou se eu já estava
  //    perto do fim; lendo antigas, não puxa (a bolinha desce).
  //  - Carga inicial: cola no fim.
  const nearBottomRef = useRef(true);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const pendingRestoreRef = useRef<{ h: number; top: number } | null>(null);
  const prevLastIdRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const last = items.length > 0 ? items[items.length - 1] : null;
    const lastId = last?.id ?? null;
    if (pendingRestoreRef.current) {
      const p = pendingRestoreRef.current;
      pendingRestoreRef.current = null;
      el.scrollTop = el.scrollHeight - p.h + p.top;
    } else if (lastId !== prevLastIdRef.current) {
      const initial = prevLastIdRef.current === null;
      if (initial || last?.from_me || nearBottomRef.current) el.scrollTop = el.scrollHeight;
    }
    prevLastIdRef.current = lastId;
  }, [items]);

  // Rolagem: atualiza near-bottom (bolinha) e dispara a página anterior ao
  // encostar no topo (50 em 50, igual o atendimento).
  const onScroll = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    const nb = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    nearBottomRef.current = nb;
    setShowScrollDown(!nb);
    if (el.scrollTop < 80 && threadQ.hasNextPage && !threadQ.isFetchingNextPage) {
      pendingRestoreRef.current = { h: el.scrollHeight, top: el.scrollTop };
      void threadQ.fetchNextPage();
    }
  };

  // Mídia carrega DEPOIS do render (skeleton → foto) e empurra a altura — o
  // scroll acima fica curto e a conversa abre "no meio". Mesmo problema que o
  // ThreadPanel resolveu com ResizeObserver: enquanto o usuário está perto do
  // fim, qualquer crescimento do conteúdo re-cola no fim; rolou pra cima pra
  // ler antigas, o observer não puxa.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller || !content) return;
    const ro = new ResizeObserver(() => {
      if (nearBottomRef.current) scroller.scrollTop = scroller.scrollHeight;
    });
    ro.observe(content);
    return () => {
      ro.disconnect();
    };
  }, []);

  const sendMut = useMutation({
    mutationFn: (input: { body?: string; media_id?: number }) =>
      sendInternalMessage({ recipient_user_id: peerId, ...input }),
    onSuccess: (msg) => {
      // Encaixa na página mais recente (o eco realtime deduplica por id).
      qc.setQueryData<InternalThreadData>(threadKey(peerId), (prev) => {
        const first = prev?.pages[0];
        if (!prev || !first) return prev;
        if (prev.pages.some((p) => p.items.some((m) => m.id === msg.id))) return prev;
        return {
          ...prev,
          pages: [
            {
              ...first,
              items: [
                ...first.items,
                {
                  ...msg,
                  from_me: true,
                  read_at: null,
                  // O colega online pode ter respondido "recebi" antes deste POST voltar.
                  delivered_at: tirarEntregaAntecipada(msg.id),
                },
              ],
            },
            ...prev.pages.slice(1),
          ],
        };
      });
      setText('');
      // Imagem enviada: o objectURL do blob vira a prévia local da bolha
      // (instantânea, sem rede) — NÃO revoga. Demais casos limpam normal.
      if (pending?.previewUrl && msg.media_id != null) {
        localPreviewByMediaId.set(msg.media_id, pending.previewUrl);
        setPending(null);
      } else {
        removePending();
      }
      requestAnimationFrame(() => inputRef.current?.focus());
    },
    onError: (err) => {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Falha ao enviar',
      );
    },
  });

  // Envia o que estiver preparado: anexo (com legenda opcional) OU só texto.
  const submit = (): void => {
    const body = text.trim();
    if (sendMut.isPending) return;
    if (pending) {
      sendMut.mutate({ media_id: pending.mediaId, ...(body && { body }) });
      return;
    }
    if (!body) return;
    sendMut.mutate({ body });
  };

  // Prepara o anexo (drop/clip): valida com as MESMAS regras do composer
  // padrão (imagem via compressImage ≤5MB, vídeo 16MB, demais 100MB), sobe
  // pro storage e deixa a prévia acima do composer. NÃO envia — envio é no
  // botão. Sem gravação de áudio.
  const stageFile = async (file: File): Promise<void> => {
    if (uploading) return;
    setUploading(true);
    try {
      let blob: Blob = file;
      let dims: { width?: number; height?: number } = {};
      const isImage = file.type.startsWith('image/');
      if (isImage) {
        try {
          // forceJpeg: MESMO parâmetro do composer padrão — sempre re-encoda
          // pra JPEG q85 (sem ele, imagem ≤5MB passava intocada: 2,1MB
          // entrava e saía 2,1MB, report do dono).
          const c = await compressImage(file, { forceJpeg: true });
          blob = c.blob;
          if (c.width != null && c.height != null) dims = { width: c.width, height: c.height };
        } catch {
          blob = file; // imagem exótica — tenta original
        }
        if (blob.size > 5 * 1024 * 1024) {
          toast.error('Imagem muito grande (máx 5MB)');
          return;
        }
      } else if (file.type.startsWith('video/')) {
        if (file.size > 16 * 1024 * 1024) {
          toast.error('Vídeo muito grande (máx 16MB)');
          return;
        }
      } else if (file.size > 100 * 1024 * 1024) {
        toast.error('Arquivo muito grande (máx 100MB)');
        return;
      }
      const up = await uploadMedia(blob, file.name, dims);
      if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
      setPending({
        mediaId: up.media_id,
        filename: file.name,
        size: blob.size,
        mime: file.type,
        previewUrl: isImage ? URL.createObjectURL(blob) : '',
      });
      requestAnimationFrame(() => inputRef.current?.focus());
    } catch (err) {
      toast.error(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title)
          : 'Falha ao preparar o arquivo',
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    // Wrapper = ZONA DE DROP inteira (header+mensagens+composer), igual o
    // ThreadPanel do atendimento — arrastar em qualquer ponto da conversa
    // mostra o overlay. O dragLeave só limpa quando sai DO WRAPPER (checagem
    // do relatedTarget evita o flicker ao passar sobre filhos).
    <div
      className="relative flex min-h-0 flex-1 flex-col"
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) void stageFile(file);
      }}
    >
      {/* Overlay de drop — MESMA tela do atendimento. O drop prepara o
          anexo (prévia + botão), não envia direto. */}
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center bg-brand-500/10 backdrop-blur-[1px]">
          <div className="flex flex-col items-center gap-3 rounded-lg border-2 border-brand-500 border-dashed bg-white/95 px-10 py-8 shadow-lg">
            <Upload size={36} strokeWidth={1.75} className="text-brand-500" />
            <div className="text-center">
              <p className="font-semibold text-[15px] text-n-900">Solte para enviar</p>
              <p className="mt-1 text-[12px] text-n-500">Imagem, vídeo, áudio ou documento</p>
            </div>
          </div>
        </div>
      )}

      <div
        data-drag-handle
        className="flex flex-shrink-0 cursor-move select-none items-center gap-2 border-b border-n-200 px-3 py-2.5"
      >
        <button
          type="button"
          onClick={backToList}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
          aria-label="Voltar"
        >
          <ArrowLeft size={16} />
        </button>
        {peer && <PeerAvatar user={peer} />}
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-[13px] text-n-900">
            {peer?.name ?? `Usuário #${peerId}`}
          </div>
          <div className={`text-[11px] ${peer?.online ? 'text-success-600' : 'text-n-400'}`}>
            {peer?.online ? 'online' : 'offline'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
          aria-label="Fechar"
        >
          <X size={16} />
        </button>
      </div>

      <div className="relative min-h-0 flex-1">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="h-full overflow-y-auto bg-n-50 px-3 py-3"
        >
          {/* Wrapper observado pelo ResizeObserver — cresceu (mídia carregou),
            re-cola no fim se o usuário estava lá. */}
          <div ref={contentRef}>
            {threadQ.isFetchingNextPage && (
              <div className="py-2 text-center text-[11px] text-n-500">Carregando anteriores…</div>
            )}
            {threadQ.isLoading && (
              <div className="py-8 text-center text-[13px] text-n-500">Carregando…</div>
            )}
            {!threadQ.isLoading && items.length === 0 && (
              <div className="py-8 text-center text-[13px] text-n-500">
                Comece a conversa com {peer?.name ?? 'o colega'} 👋
              </div>
            )}
            {items.map((m, i) => {
              const prev = items[i - 1];
              const showDay = !prev || dayLabel(prev.created_at) !== dayLabel(m.created_at);
              return (
                <div key={m.id}>
                  {showDay && (
                    <div className="my-2 flex justify-center">
                      <span className="rounded-pill bg-n-200 px-2.5 py-0.5 text-[10px] text-n-600">
                        {dayLabel(m.created_at)}
                      </span>
                    </div>
                  )}
                  {/* Cores = MESMO padrão do MessageBubble do atendimento: enviada
                  fica verde-claro com texto escuro FIXO nos 2 temas
                  (force-light); recebida usa as vars --bubble-recv-* que
                  viram azul-escuro no modo noturno. */}
                  <div className={`mb-1.5 flex ${m.from_me ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[80%] rounded-[7px] px-3 py-2 shadow-xs ${
                        m.from_me
                          ? 'force-light rounded-tr-none border border-[#86efb8] bg-[#c3f7d8] text-[#09090B]'
                          : 'rounded-tl-none border border-[var(--bubble-recv-border)] bg-[var(--bubble-recv-bg)] text-[var(--bubble-recv-text)]'
                      }`}
                    >
                      {m.media_id != null && (
                        <MediaBubble
                          message={m}
                          onOpenImage={(src, name) => {
                            setLightbox({ src, ...(name && { name }) });
                            setLightboxOpen(true);
                          }}
                          onOpenPdf={(url, filename) => {
                            setPdfPreview({ url, filename });
                            setLightboxOpen(true);
                          }}
                        />
                      )}
                      {m.body && (
                        <div className="whitespace-pre-wrap break-words text-[13px] leading-[1.35]">
                          {m.body}
                        </div>
                      )}
                      <div
                        className={`mt-0.5 flex items-center justify-end gap-1 text-[9px] ${
                          m.from_me ? 'text-[#71717A]' : 'text-[var(--bubble-recv-meta)]'
                        }`}
                      >
                        {timeShort(m.created_at)}
                        {m.from_me && <VistosInternos m={m} />}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bolinha de descer — aparece quando rolou pra cima (igual atendimento). */}
        {showScrollDown && (
          <button
            type="button"
            onClick={() => {
              const el = scrollRef.current;
              if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            }}
            className="absolute right-3 bottom-3 z-20 flex h-[40px] w-[40px] items-center justify-center rounded-pill bg-brand-100 text-brand-700 shadow-md transition hover:bg-brand-200"
            aria-label="Descer pro fim da conversa"
          >
            <ChevronDown size={18} strokeWidth={2} />
          </button>
        )}
      </div>

      {/* Prévia do anexo preparado — mesmo padrão do composer padrão:
          thumb/ícone + nome + tamanho + X. O envio acontece no botão. */}
      {pending && (
        <div className="flex flex-shrink-0 items-center gap-3 border-t border-n-200 px-3 py-2">
          {pending.previewUrl ? (
            <img
              src={pending.previewUrl}
              alt={pending.filename}
              className="h-12 w-12 rounded object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded bg-brand-50 text-brand-500">
              <Paperclip size={18} />
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col text-[12px]">
            <span className="truncate font-medium text-n-800">{pending.filename}</span>
            <span className="text-n-500">{(pending.size / 1024).toFixed(0)} KB</span>
          </div>
          <button
            type="button"
            onClick={removePending}
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
            aria-label="Remover anexo"
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div
        className={`flex flex-shrink-0 items-end gap-2 px-3 py-2.5 ${
          pending ? 'border-t border-n-100' : 'border-t border-n-200'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void stageFile(file);
          }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-md text-n-500 transition hover:bg-n-100 disabled:opacity-50"
          title="Anexar arquivo"
          aria-label="Anexar arquivo"
        >
          {uploading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Paperclip size={18} strokeWidth={1.75} />
          )}
        </button>
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Guard de dead-key PT-BR (lição do duplo-envio no Composer).
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={pending ? 'Legenda (opcional)' : 'Digite sua mensagem'}
          rows={1}
          className="max-h-[96px] min-h-[38px] flex-1 resize-none rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
        />
        <button
          type="button"
          onClick={submit}
          disabled={sendMut.isPending || (!text.trim() && !pending)}
          className="flex h-[38px] w-[38px] flex-shrink-0 items-center justify-center rounded-pill bg-brand-500 text-white transition hover:bg-brand-600 disabled:opacity-40"
          aria-label="Enviar"
        >
          {sendMut.isPending ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <SendHorizontal size={16} />
          )}
        </button>
      </div>

      {lightbox && (
        <ImageLightbox
          src={lightbox.src}
          {...(lightbox.name && { filename: lightbox.name })}
          onClose={() => {
            setLightbox(null);
            setLightboxOpen(false);
          }}
        />
      )}

      {pdfPreview && (
        <PdfPreviewModal
          open
          url={pdfPreview.url}
          filename={pdfPreview.filename}
          onClose={() => {
            setPdfPreview(null);
            setLightboxOpen(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * Anexo dentro da bolha. Imagem/vídeo buscam a signed URL ao montar (TTL 10min,
 * cacheada 8min); documento só busca no clique (abre em nova aba).
 */
/**
 * Vistos da mensagem que EU mandei, igual ao WhatsApp e ao balão do
 * Atendimento: ✓ enviada, ✓✓ cinza entregue (o ChatPlus do colega recebeu),
 * ✓✓ azul lida (ele abriu a conversa). Lida implica entregue.
 */
function VistosInternos({
  m,
  size = 13,
}: {
  m: { delivered_at?: string | null; read_at?: string | null };
  size?: number;
}) {
  if (m.read_at) {
    return (
      <span title="Lida">
        <CheckCheck size={size} className="text-brand-500" aria-label="Lida" />
      </span>
    );
  }
  if (m.delivered_at) {
    return (
      <span title="Entregue">
        <CheckCheck size={size} aria-label="Entregue" />
      </span>
    );
  }
  return (
    <span title="Enviada">
      <Check size={size} aria-label="Enviada" />
    </span>
  );
}

function MediaBubble({
  message,
  onOpenImage,
  onOpenPdf,
}: {
  message: InternalMessage;
  onOpenImage: (src: string, name?: string) => void;
  onOpenPdf: (url: string, filename: string) => void;
}) {
  const mime = message.media?.mime_type ?? '';
  const isImage = mime.startsWith('image/');
  const isVideo = mime.startsWith('video/');
  const isPdf = mime.toLowerCase().includes('pdf');
  // Imagem que EU enviei nesta aba: bytes já na memória (objectURL) —
  // renderiza pronta na hora, sem rede.
  const localSrc =
    isImage && message.media_id != null ? localPreviewByMediaId.get(message.media_id) : undefined;

  const urlQ = useQuery({
    queryKey: ['internal-media-url', message.id],
    queryFn: () => getInternalMediaUrl(message.id),
    enabled: (isImage || isVideo) && !localSrc,
    staleTime: 8 * 60_000,
  });

  if (isImage) {
    // Caixa no TAMANHO FINAL (aspect-ratio das dims do payload, padrão do
    // MessageBubble do atendimento): a bolha nasce pronta e o layout nunca
    // remonta — a foto "pinta" dentro da caixa cinza quando baixa. Sem dims
    // (GIF/legado): caixa padrão 200×140 com object-cover.
    const w = message.media?.width ?? null;
    const h = message.media?.height ?? null;
    let boxW = 200;
    let boxH = 140;
    if (w && h) {
      const scale = Math.min(220 / h, 240 / w, 1);
      boxW = Math.max(60, Math.round(w * scale));
      boxH = Math.max(60, Math.round(h * scale));
    }
    const src = localSrc ?? urlQ.data?.url;
    return (
      <button
        type="button"
        onClick={() => {
          if (src) onOpenImage(src, message.media?.original_name ?? undefined);
        }}
        className="mb-1 block cursor-pointer"
        style={{ width: boxW, height: boxH }}
        aria-label="Abrir imagem"
      >
        <div className="h-full w-full overflow-hidden rounded-md bg-n-200">
          {src && (
            <img
              src={src}
              alt={message.media?.original_name ?? ''}
              className="h-full w-full object-cover"
            />
          )}
        </div>
      </button>
    );
  }

  if (isVideo) {
    if (!urlQ.data) {
      return <div className="mb-1 h-[140px] w-[200px] animate-pulse rounded-md bg-n-200" />;
    }
    return (
      // biome-ignore lint/a11y/useMediaCaption: vídeo de colega, sem legenda disponível
      <video src={urlQ.data.url} controls className="mb-1 max-h-[220px] max-w-full rounded-md" />
    );
  }

  // Documento/qualquer outro tipo: chip com nome + tamanho. PDF abre no
  // viewer fullscreen (PdfPreviewModal, mesmo do atendimento); o resto em
  // nova aba pela signed URL.
  const name = message.media?.original_name ?? 'arquivo';
  const size = message.media?.size_bytes ?? null;
  return (
    <button
      type="button"
      onClick={() => {
        void getInternalMediaUrl(message.id)
          .then((r) => {
            if (isPdf) onOpenPdf(r.url, name);
            else window.open(r.url, '_blank', 'noopener');
          })
          .catch(() => toast.error('Falha ao abrir o arquivo'));
      }}
      className="mb-1 flex w-full items-center gap-2 rounded-md border border-black/10 bg-black/5 px-2.5 py-2 text-left transition hover:bg-black/10"
    >
      <FileText size={18} strokeWidth={1.75} className="flex-shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-[12px]">{name}</span>
        {size != null && <span className="block text-[10px] opacity-70">{formatBytes(size)}</span>}
      </span>
    </button>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ───────────────────────── Helpers ─────────────────────────

function PeerAvatar({ user }: { user: UserRow }) {
  return (
    <div className="relative flex-shrink-0">
      <div className="relative flex h-[36px] w-[36px] items-center justify-center overflow-hidden rounded-pill bg-n-100 text-n-400">
        <User size={20} strokeWidth={1.5} />
        {user.avatar_url && (
          <img
            src={user.avatar_url}
            alt=""
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
      </div>
      <span
        className={`-right-0.5 -bottom-0.5 absolute h-3 w-3 rounded-pill border-2 border-n-0 ${
          user.online ? 'bg-success-500' : 'bg-n-400'
        }`}
        aria-hidden="true"
      />
    </div>
  );
}

function timeShort(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Hoje';
  if (d.toDateString() === yesterday.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
